import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useErrorToast } from '~/common/errors/useErrorToast';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { EsignatureType } from '~/enums/EsignatureType';
import { TicketPermissionType } from '~/enums/TicketPermissionType';
import {
  EsignatureEnvelopeStatusValue,
  EsignatureRecipient,
  EsignatureSignerStatusValue,
} from '~/features/ticket/types/esignature';
import { api } from '~/redux/api/createApi';
import { selectTicketId, selectTicketPermissions } from '~/redux/selectors';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';

export const useSendReminder = (recipient: EsignatureRecipient) => {
  const { toast: errorToast } = useErrorToast();
  const { toast } = useToast();
  const intl = useIntl();
  const ticketId = useSelector(selectTicketId);
  const currentUser = useSelector(
    slices.currentUser.selectors.selectCurrentUser,
  );
  const [sendReminder] = api.endpoints.sendReminder.useMutation();
  const ticketPermissions = useSelector(selectTicketPermissions);
  const permissionToRemind = ticketPermissions.includes(
    TicketPermissionType.RemindSignatories,
  );
  const { data: envelope } = api.endpoints.getEnvelope.useQueryState(
    {
      ticketId,
    },
    {
      skip: !ticketId || !permissionToRemind,
    },
  );
  const { data: esignatureTokenStatus } =
    api.endpoints.getTokenStatus.useQueryState(
      {
        ticketId,
      },
      {
        skip: !ticketId,
      },
    );
  const vendor = esignatureTokenStatus?.providerType;
  const [lastReminderDate, setLastReminderDate] =
    useState<Nullable<Date>>(null);
  const isEnvelopeSentForSigning =
    envelope?.status.value === EsignatureEnvelopeStatusValue.Sent;
  const isSignerPending =
    recipient.status?.value === EsignatureSignerStatusValue.Pending;

  return {
    showSendReminder:
      currentUser.id !== recipient.userId &&
      permissionToRemind &&
      isEnvelopeSentForSigning &&
      isSignerPending &&
      vendor,
    lastReminderDate,
    sendReminder: async () => {
      try {
        await sendReminder({
          ticketId,
          recipientId: recipient.id,
          // send remind button will not be shown if vendor is not set (as determined by showSendReminder)
          vendor: vendor as EsignatureType,
        }).unwrap();
        setLastReminderDate(new Date());
        toast({
          message: intl.formatMessage({
            id: 'SendReminder.successMessage',
            defaultMessage: 'Reminder sent successfully.',
            description:
              'Success toast message when a signing reminder is sent.',
          }),
          status: 'success',
        });
      } catch (error) {
        if (error instanceof Error) {
          errorToast(error, {
            autoDismiss: false,
          });
        }
      }
    },
  };
};
